import React, {useEffect, useMemo, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableButton from 'components/atoms/Buttons/TableButton'
import {ClientName} from 'components/atoms/ClientName'
import Pagination from 'components/molcules/Pagination'
import TableCell from 'components/Table/TableCell'
import TableReportSearch from 'components/Table/TableReportSearch'
import TableLoading from 'components/Table/TableLoading'
import TableEmpty from 'components/Table/TableEmpty'
import {useEegSummaryFiveIndicatorsSearch} from 'features/analysis/useEegSummarySearch'
import {finalizeEegFiveIndicator, FiveIndicatorResponse} from 'api/analysisApi'
import {AnalysisReportType} from 'constants/AnalysisConstant'
import {getEegSummaryReportPathV1} from 'constants/RouteConstant'
import {statusToI18nString} from 'helpers/analysisHelper'
import {getQueryFromUrl} from 'helpers/commonHelper'
import {
  dateToPeriodString,
  getDefaultPeriodDate,
  isoStringToDateAndTimeString,
} from 'helpers/dateHelper'
import openInNewTab from 'helpers/openInNewTab'
import {DEFAULT_SEARCH} from 'constants/CommonConstant'
import {TYPE_REPORT} from 'pages/Analysis/EEGSummary/constants'
import useStyles from 'components/Table/useTableStyles'
import useToolbarStyles from 'components/Table/useTableToolbarStyles'
import TableCellReport from 'components/Table/TableCellReport'
import useFailureModal from 'features/modal/useFailureModal'

interface TableToolbarProps {
  isActive: boolean
}
function TableToolbar({isActive}: TableToolbarProps) {
  const location = useLocation()
  const classes = useToolbarStyles()
  const {query, search, onSearch} = useEegSummaryFiveIndicatorsSearch()
  const selection = getDefaultPeriodDate()
  const [key, setKey] = useState(0)

  useEffect(() => {
    if (isActive) {
      const initValue = getQueryFromUrl(location.search)
      onSearch({
        ...query,
        period: dateToPeriodString(selection),
        paging: {page: 0, size: 10},
        ...initValue,
        ...DEFAULT_SEARCH,
      })
      setKey(key + 1)
    }
  }, [isActive])

  return (
    <div className={classes.root}>
      <TableReportSearch
        key={key}
        reportType={AnalysisReportType.EEGSummary}
        isRoot={false}
        query={query}
        search={search}
        onSearch={onSearch}
      />
    </div>
  )
}

interface RenderTableRowProps {
  item: FiveIndicatorResponse
}

function RenderTableRow({item}: RenderTableRowProps) {
  const {t} = useTranslation()

  const classes = useToolbarStyles()
  const {query, onSearch} = useEegSummaryFiveIndicatorsSearch()
  const {onOpen: handleFailureModalOpen} = useFailureModal()

  const handleResultClick = (event: any) => {
    event.stopPropagation()
    openInNewTab(
      getEegSummaryReportPathV1(
        item.id,
        undefined,
        TYPE_REPORT.FIVE_INDICATORS,
      ),
    )
  }

  const handleMainReportClick = async () => {
    const {id} = item
    try {
      await finalizeEegFiveIndicator(id)
      onSearch({...query})
    } catch (err) {
      handleFailureModalOpen(err.message)
    }
  }

  const renderEyeStatus = (item: FiveIndicatorResponse) => {
    if (item.ecId) return t('IEC')
    return '-'
  }

  return (
    <TableRow hover>
      <TableCell align='center' padding='none'>
        {item.id}
      </TableCell>
      <TableCell align='center' padding='none'>
        {item.ecId}
      </TableCell>
      <TableCell align='center' padding='none'>
        {item.reEcId}
      </TableCell>
      <TableCellReport
        align='center'
        padding='none'
        reportId={item.id}
        isMainReport={item.isMain}
        onClick={handleMainReportClick}
      >
        {item.isMain}
      </TableCellReport>
      <TableCell align='center' padding='none'>
        <ClientName
          firstName={item.patientFirstName}
          lastName={item.patientLastName}
        />
      </TableCell>
      <TableCell align='center' padding='none'>
        {renderEyeStatus(item)}
      </TableCell>
      <TableCell align='center' padding='none'>
        <div className={classes.fileName}>{item.ecFileName}</div>
      </TableCell>
      <TableCell align='center' padding='none'>
        {isoStringToDateAndTimeString(String(item.createdDate))}
      </TableCell>
      <TableCell align='center' padding='none'>
        {item.dbVersion}
      </TableCell>
      <TableCell align='center' padding='none'>
        {t(statusToI18nString(item.status as AnalysisStatus))}
      </TableCell>
      <TableCell align='center' padding='none'>
        <TableButton
          onClick={handleResultClick}
          disabled={item.status !== 'SUCCESS'}
        >
          {t('IRead')}
        </TableButton>
      </TableCell>
    </TableRow>
  )
}
interface FiveIndicatorsProps {
  readonly isActive: boolean
}
function FiveIndicators({isActive}: FiveIndicatorsProps) {
  const {t} = useTranslation()
  const classes = useStyles()
  const {loading, error, query, paging, pagingInfo, items, onSearch} =
    useEegSummaryFiveIndicatorsSearch()

  const HEAD_TABLE = useMemo(
    () => [
      t('IAnalysisNo'),
      t('IEEGIndividualIndex'),
      t('IReAnalysis_no'),
      t('IMainReport'),
      t('IPatientName'),
      t('IECEO'),
      t('IFileName'),
      t('IMeasureDate'),
      t('IVersion'),
      t('IProgress'),
      t('IAnalysisReport'),
    ],
    [t],
  )

  const emptyRows = items === null ? 5 : 5 - items.length
  const setPageIndex = (page: number) =>
    onSearch({
      ...query,
      paging: {
        page,
        size: paging.size,
      },
    })

  const onSizeChanged = (
    event: React.ChangeEvent<{name?: string; value: unknown}>,
  ) => {
    const size = parseInt(event.target.value as string, 10)
    onSearch({
      ...query,
      paging: {
        page: 0,
        size,
      },
    })
  }
  return (
    <TableContainer className={classes.tableContainer}>
      <TableToolbar isActive={isActive} />
      <Table className={classes.table}>
        <TableHead>
          <TableRow className={classes.tableHeader}>
            {HEAD_TABLE.map((item) => (
              <TableCell
                align='center'
                padding='none'
                key={item}
                style={{
                  whiteSpace: 'pre-line',
                }}
              >
                {item}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <RenderTableRow key={item.id} item={item} />
          ))}
          <TableLoading loading={loading} emptyRows={emptyRows} />
          <TableEmpty
            emptyRows={emptyRows}
            loading={loading}
            error={error}
            isEmpty={items.length === 0}
          />
        </TableBody>
      </Table>
      <Pagination
        totalPageCount={pagingInfo.totalPages}
        currentPageIndex={query.paging.page ?? 0}
        itemCountPerPage={query.paging.size ?? 10}
        setCurrentPageIndex={setPageIndex}
        loading={loading}
        onItemCountPerPageChanged={onSizeChanged}
      />
    </TableContainer>
  )
}

export default FiveIndicators
